import type * as z from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useCallback, useEffect } from 'react'
import { RadioGroup, Textarea } from '@heroui/react'
import { type DbUser, stepProgramSchema } from '@leangap/types'
import StepHeader from '@/components/account/StepHeader'
import { useStepStatusSync } from '@/store/useStepStatusSync'
import { appStore, useAppStore, isApplicationLocked } from '@/store'
import { useFormVisitValidation } from '@/hooks/useFormVisitValidation'
import { TEXTAREA_MAX_CHARS, TEXTAREA_MIN_ROWS } from '@/constants/form'
import OutsideLabel from '@/components/account/OutsideLabel'
import CustomRadio from '@/components/account/CustomRadio'
import { PROGRAM_OPTIONS } from '@/constants/program'

type StepProgramForm = z.infer<typeof stepProgramSchema>

export default function ProgramStep() {
	// single source of truth → app store
	const { user } = useAppStore()
	if (!user) return null
	return <ProgramForm user={user} />
}

function ProgramForm({ user }: { user: DbUser }) {
	// Default values function (robust, like General.tsx)
	const toDefaults = useCallback(
		(u: DbUser): StepProgramForm => ({
			name: (u.program_name as StepProgramForm['name']) ?? '',
			priority_choice: u.priority_choice ?? '',
			summer_commitments: u.summer_commitments ?? '',
		}),
		[]
	)

	const form = useForm<StepProgramForm>({
		resolver: zodResolver(stepProgramSchema),
		defaultValues: toDefaults(user),
		mode: 'onChange',
		criteriaMode: 'all',
	})

	const {
		register,
		setValue,
		watch,
		formState: { errors, isValid, isDirty },
	} = form

	useFormVisitValidation('program', form)
	useStepStatusSync('program', isValid, isDirty, form.formState.isSubmitted)

	// Register program_name without binding to a native input
	// we'll drive it via RadioGroup (controlled) + setValue.
	useEffect(() => {
		register('name')
	}, [register])

	// map this step to a flat DbUser patch for the global store
	const mapToDbPatch = useCallback(
		(v: StepProgramForm): Partial<DbUser> => ({
			program_name: v.name || null, // 'Online' | 'San Francisco'
			priority_choice: v.priority_choice || null,
			summer_commitments: v.summer_commitments || null,
		}),
		[]
	)

	// Push changes into the global store → autosave handles persistence
	useEffect(() => {
		const current = appStore.snapshot().user
		if (current?.application_process === 'Submitted') return
		const sub = watch((values) => appStore.patch(mapToDbPatch(values as StepProgramForm)))
		return () => sub.unsubscribe()
	}, [watch, mapToDbPatch])

	const programValue = watch('name')

	const isSubmitted = isApplicationLocked(user?.application_process)

	// Debug output for form validity and dirtiness
	return (
		<>
			<StepHeader title="Program Choice" lead="Please choose your priority program" />
			<form className="mt-8 space-y-6 pb-12">
				<p>
					While the content, impact, value, networking, and outcomes are{' '}
					<span className="underline">identical</span> across both programs, you have the
					opportunity to select a priority program that aligns with your preferred learning modality
					and availability.
				</p>
				<p>
					Admitted students have access to both programs. Upon admission, you can secure your spot
					in either program by paying its tuition, as long as there is availability.
				</p>

				<div className="space-y-8">
					<RadioGroup
						isReadOnly={isSubmitted}
						isRequired
						value={programValue}
						errorMessage={errors.name?.message}
						isInvalid={!!errors.name}
						name="type-program"
						onValueChange={(val) => {
							// Always mark as dirty and validate, even when clearing
							setValue('name', val as StepProgramForm['name'], {
								shouldDirty: true,
								shouldValidate: true,
							})
						}}
					>
						{PROGRAM_OPTIONS.map((opt) => (
							<div key={opt.key}>
								<CustomRadio key={opt.key} value={opt.value} description={opt.description}>
									{opt.label}
								</CustomRadio>
								<div className="mt-1 mb-2 w-full pl-2 text-sm text-neutral-400">{opt.note}</div>
							</div>
						))}
					</RadioGroup>

					<Textarea
						isReadOnly={isSubmitted}
						isRequired
						className="w-full"
						errorMessage={errors.priority_choice?.message}
						isInvalid={!!errors.priority_choice}
						label={<OutsideLabel>Why are you interested in your priority choice?</OutsideLabel>}
						labelPlacement="outside"
						maxLength={TEXTAREA_MAX_CHARS}
						minRows={TEXTAREA_MIN_ROWS}
						placeholder="Enter your answer"
						{...register('priority_choice', {
							required: 'This field is required.',
						})}
					/>

					<Textarea
						isReadOnly={isSubmitted}
						isRequired
						className="w-full"
						errorMessage={errors.summer_commitments?.message}
						isInvalid={!!errors.summer_commitments}
						label={
							<OutsideLabel>
								Do you have any other commitments or activities planned this summer?
							</OutsideLabel>
						}
						labelPlacement="outside"
						maxLength={TEXTAREA_MAX_CHARS}
						minRows={TEXTAREA_MIN_ROWS}
						placeholder="Enter your answer"
						{...register('summer_commitments', {
							required: 'This field is required.',
						})}
					/>
				</div>
			</form>
		</>
	)
}
